import { ArrowRight, ChevronLeft, Download, X } from 'lucide-react'
import { useState } from 'react'
import type React from 'react'
import {
	LandlordTermsDocumentViewer,
	landlordTermsViewUrl,
} from '#app/components/screening/LandlordTermsDocumentViewer'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { assets } from '#app/data/assets'
import { TERMS_OF_SERVICE_HTML } from '#app/data/terms-content'

export type LandlordTermsConsent = {
	fileName?: string | null
	blobUrl: string
}

interface ScreeningConsentModalProps {
	onConsent: () => void
	onClose?: () => void
	isSubmitting?: boolean
	/** Org T&Cs from application form config (when enabled + file uploaded). */
	landlordTerms?: LandlordTermsConsent | null
}

type ModalView = 'consent' | 'terms' | 'credit-check' | 'landlord-terms'

const CREDIT_CHECK_CONTENT = [
	'assessing the Applicant’s eligibility for tenancy;',
	'verifying the Applicant’s ongoing suitability and financial responsibility during the tenancy, including periodic account review; and',
	'enforcing the terms of any tenancy agreement, including the collection of any outstanding amounts, arrears, damages, or other debts owed by the Applicant.',
	'The Applicant acknowledges and agrees that this authorization shall remain valid and in effect during the course of the tenancy and thereafter, for so long as any amounts remain outstanding or the Landlord has a lawful purpose related to the tenancy, including but not limited to debt recovery and collection activities.',
	'The Applicant expressly authorizes the Landlord and/or the Landlord’s agent to obtain updated credit information and to make such inquiries as may be necessary to locate the Applicant and/or enforce payment obligations in the event of default.',
]

function DetailView({
	title,
	children,
	onBack,
	onClose,
	/** When true, only children scroll (used for PDF preview). */
	scrollChildrenOnly = false,
}: {
	title: string
	children: React.ReactNode
	onBack: () => void
	onClose: () => void
	scrollChildrenOnly?: boolean
}) {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex shrink-0 items-center justify-between px-6 pt-5 pb-2">
				<button
					type="button"
					onClick={onBack}
					className="flex cursor-pointer items-center gap-1 text-sm text-[#32302F] hover:text-[#32302F]"
				>
					<ChevronLeft className="h-4 w-4" />
					Back to consent
				</button>
				<button
					type="button"
					onClick={onClose}
					className="cursor-pointer rounded-full p-1 hover:bg-gray-100"
				>
					<X className="h-5 w-5 text-[#4F4F4F]" />
				</button>
			</div>

			<div
				className={
					scrollChildrenOnly
						? 'flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-2'
						: 'min-h-0 flex-1 overflow-y-auto px-6 pb-2'
				}
			>
				<h2 className="font-calgary mb-2 shrink-0 text-[28px] text-[#32302F]">
					{title}
				</h2>
				{children}
			</div>

			<div className="flex shrink-0 justify-end p-6">
				<button
					type="button"
					onClick={onBack}
					className="cursor-pointer rounded-[30px] bg-[#32302F] px-8 py-3 text-[15px] font-medium text-white hover:bg-[#1a1918]"
				>
					Close
				</button>
			</div>
		</div>
	)
}

function ConsentCheck({
	checked,
	onToggle,
}: {
	checked: boolean
	onToggle: () => void
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-colors ${
				checked ? 'border-[#16A354] bg-[#16A354]' : 'border-[#C4C4C4]'
			}`}
		>
			{checked && (
				<svg
					aria-hidden="true"
					width="10"
					height="8"
					viewBox="0 0 10 8"
					fill="none"
				>
					<path
						d="M1 4L3.5 6.5L9 1"
						stroke="white"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)}
		</button>
	)
}

export function ScreeningConsentModal({
	onConsent,
	onClose,
	isSubmitting = false,
	landlordTerms = null,
}: ScreeningConsentModalProps) {
	const [view, setView] = useState<ModalView>('consent')
	const [termsAccepted, setTermsAccepted] = useState(false)
	const [creditCheckAccepted, setCreditCheckAccepted] = useState(false)
	const [landlordAccepted, setLandlordAccepted] = useState(false)

	const showLandlordTerms = Boolean(landlordTerms?.blobUrl)
	const allAccepted =
		termsAccepted &&
		creditCheckAccepted &&
		(!showLandlordTerms || landlordAccepted)

	const handleConsent = () => {
		if (!isSubmitting && allAccepted) {
			onConsent()
		}
	}

	const handleClose = () => {
		onClose?.()
	}

	return (
		// TWO fixed layers, on purpose (client 2026-07-28):
		//  - the backdrop stays z-40, BELOW the app layout's NavBar (z-50), so the
		<>
			<div className="fixed inset-0 z-40 overflow-hidden">
				{/* Property image */}
				<div
					className="absolute inset-0 scale-105 bg-cover bg-center blur-[3px]"
					style={{
						backgroundImage: `url('${assets.screening.consentBackground}')`,
					}}
				/>

				{/* #32302F 50% veil wrapping BOTH the header and the image */}
				<div className="pointer-events-none absolute inset-0 z-20 bg-[#32302F]/50" />
			</div>

			<div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto p-4">
				<div
					className={`pointer-events-auto flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-[#F3F3F3] bg-white shadow-xl ${view === 'consent' ? 'max-w-lg' : 'max-w-4xl'}`}
				>
					{view === 'consent' ? (
						<>
							{/* Header */}
							<div className="flex shrink-0 items-start justify-between px-6 pt-6 pb-2">
								<div>
									<h2 className="font-calgary text-[28px] text-[#32302F]">
										Consent Form
									</h2>
									<p className="font-regular mt-1 text-[15px] leading-[18px] font-medium text-[#32302F]">
										Please review and accept the following agreements to proceed
										with your application
									</p>
								</div>
								<button
									type="button"
									onClick={handleClose}
									className="mt-1 cursor-pointer rounded-full p-1 hover:bg-gray-100"
								>
									<X className="h-5 w-5 text-[#4F4F4F]" />
								</button>
							</div>

							{/* Consent Items */}
							<div className="flex-1 space-y-3 px-6 py-4">
								{/* Terms of Service */}
								<div className="flex items-center justify-between rounded-xl border border-[#E9E9E9] px-5 py-4">
									<span className="text-[15px] text-[#32302F]">
										I agree to the{' '}
										<button
											type="button"
											onClick={() => setView('terms')}
											className="cursor-pointer font-medium underline hover:text-[#555]"
										>
											Terms of Service
										</button>
									</span>
									<ConsentCheck
										checked={termsAccepted}
										onToggle={() => setTermsAccepted((v) => !v)}
									/>
								</div>

								{/* Credit Check Authorization */}
								<div className="flex items-center justify-between rounded-xl border border-[#E9E9E9] px-5 py-4">
									<button
										type="button"
										onClick={() => setView('credit-check')}
										className="cursor-pointer text-left text-[15px] font-medium text-[#32302F] underline hover:text-[#555]"
									>
										Credit Check Authorization
									</button>
									<ConsentCheck
										checked={creditCheckAccepted}
										onToggle={() => setCreditCheckAccepted((v) => !v)}
									/>
								</div>

								{showLandlordTerms && landlordTerms?.blobUrl ? (
									<div className="flex items-center justify-between rounded-xl border border-[#E9E9E9] px-5 py-4">
										<span className="text-[15px] text-[#32302F]">
											Landlord{' '}
											<button
												type="button"
												onClick={() => {
													// Mobile: open PDF/image in a new tab. Desktop keeps
													// the in-modal preview.
													const mobile =
														window.matchMedia('(max-width: 767px)').matches
													if (mobile) {
														window.open(
															landlordTermsViewUrl(landlordTerms.blobUrl),
															'_blank',
															'noopener,noreferrer',
														)
														return
													}
													setView('landlord-terms')
												}}
												className="cursor-pointer font-medium underline hover:text-[#555]"
											>
												Terms & Conditions
											</button>
										</span>
										<ConsentCheck
											checked={landlordAccepted}
											onToggle={() => setLandlordAccepted((v) => !v)}
										/>
									</div>
								) : null}
							</div>

							{/* Footer */}
							<div className="flex shrink-0 justify-end px-6 pt-2 pb-6">
								<AnimatedStatusButton
									type="button"
									onClick={handleConsent}
									disabled={!allAccepted || isSubmitting}
									status={isSubmitting ? 'pending' : 'idle'}
									className="rounded-[30px] bg-[#32302F] px-6 py-[12px] text-[15px] font-medium text-white hover:bg-[#1a1918] disabled:cursor-not-allowed disabled:opacity-50 sm:px-8"
								>
									Consent & Proceed
									<ArrowRight className="ml-1 h-4 w-4" />
								</AnimatedStatusButton>
							</div>
						</>
					) : view === 'terms' ? (
						<DetailView
							title="Terms of Service"
							onBack={() => setView('consent')}
							onClose={handleClose}
						>
							<div className="prose prose-sm max-w-none">
								<div
									dangerouslySetInnerHTML={{ __html: TERMS_OF_SERVICE_HTML }}
								/>
							</div>
						</DetailView>
					) : view === 'credit-check' ? (
						<DetailView
							title="Credit Check Authorization"
							onBack={() => setView('consent')}
							onClose={handleClose}
						>
							<p className="mb-4 text-sm leading-relaxed text-[#4F4F4F]">
								The Applicant hereby irrevocably consents and authorizes the
								Landlord and/or the Landlord’s agent to obtain, review, verify,
								and exchange consumer and/or credit information relating to the
								Applicant, including credit reports, credit scores, and account
								status information, from any credit reporting agency or other
								source, at any time and from time to time, for the purposes of:
							</p>
							<div className="space-y-3">
								{CREDIT_CHECK_CONTENT.map((item, i) => (
									<p key={i} className="text-sm leading-relaxed text-[#4F4F4F]">
										({String.fromCharCode(97 + i)}) {item}
									</p>
								))}
							</div>
						</DetailView>
					) : (
						<div className="flex min-h-0 flex-1 flex-col">
							{/* Match credit-report preview chrome: title + X */}
							<div className="flex shrink-0 items-center justify-between border-b border-[#F3F3F3] px-6 py-4">
								<h2 className="font-calgary text-[28px] text-[#32302F]">
									Landlord Terms & Conditions
								</h2>
								<button
									type="button"
									onClick={() => setView('consent')}
									className="cursor-pointer rounded-full p-1 hover:bg-gray-100"
									aria-label="Close"
								>
									<X className="h-5 w-5 text-[#4F4F4F]" />
								</button>
							</div>

							{landlordTerms?.blobUrl ? (
								<LandlordTermsDocumentViewer
									blobUrl={landlordTerms.blobUrl}
									fileName={landlordTerms.fileName}
								/>
							) : null}

							<div className="flex shrink-0 justify-end border-t border-[#F3F3F3] px-6 py-4">
								{landlordTerms?.blobUrl ? (
									<a
										href={landlordTermsViewUrl(landlordTerms.blobUrl, {
											disposition: 'attachment',
										})}
										className="inline-flex cursor-pointer items-center gap-2 rounded-[30px] bg-[#32302F] px-8 py-3 text-[15px] font-medium text-white hover:bg-[#1a1918]"
									>
										<Download className="h-4 w-4" />
										Download
									</a>
								) : null}
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	)
}
